import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Widget from '../../widget';
import Chart from '../../components/scl-components/stack-chart';
import Legend from '../../components/scl-components/legend';
import { legendConfig } from './config';
import { Select } from 'antd';
import styled from 'styled-components';
import { selectFilterSearch } from '../../../../helpers/StringHelper';
import { getPopupContainer } from '../../../../helpers/popUp';
import { timelineOfIncidentsOptions, timelineOfIncidentsOptionsDefault } from '../../../../helpers/filters/incidents';

const Style = styled.div`
  display: flex;
  flex-direction: column;

  .chart {
    height: 350px;
  }

  .filters {
    display: flex;
    gap: var(--size-lg);
    margin-bottom: 40px;
    flex-wrap: wrap;

    .filter {
      flex: 1;
      display: flex;
      gap: 18px;
      
      .ant-select {
        flex: 1;
        max-width: calc(100% - 78px);
      }

      .cont {
        display: flex;
        flex-direction: column;
        justify-content: center;

        label {
          margin-bottom: 0px;
          font-size: 12px;
          font-weight: 600;
          min-width: 60px;
        }
      }
    }
  }
`;

const RiskMitigation = ({
  data: allData = [],
  isSingleSource = true,
  options = [],
  language = 'en',
  riskCategories = [],
  loading,
}) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(timelineOfIncidentsOptionsDefault);
  const [riskType, setRiskType] = useState('all');
  const [legend, setLegend] = useState();

  const toggle = (item) => {
    setLegend((prev) => prev.map((p) => {
      if (item.key === p.key) {
        return { ...item, toggled: !item.toggled };
      }
      return p;
    }));
  };

  const data = useMemo(() => {
    if (Array.isArray(allData)) {
      if (riskType === 'all') {
        return allData;
      }
      return allData.filter((r) => r.risk === riskType);
    }
    return [];
  }, [allData, riskType]);

  const filteredData = useMemo(() => {
    if (legend && data) {
      return data.filter((item) => {
        const leg = legend.find((i) => i.key === item.type);
        if (leg && leg.toggled) {
          return true;
        }
        return false;
      })
    }
    return [];
  }, [legend, data]);

  useEffect(() => {
    const leg = Object.entries(legendConfig).map(([k, v]) => ({
      label: t(v.label),
      color: v.color,
      key: k,
      toggled: true,
    }));
    setLegend(leg);
  }, [t]);

  const groupedOptions = riskCategories.map((o) => ({
    label: o.label[language],
    value: o.value,
    children: options.filter((O) => O.group === o.value).map((O) => ({
      value: O.value,
      label: O.label[language],
    }))
  }));

  return (
    <Widget
      loading={loading}
      tooltip={t('Performance analysis of the corrective action plan for this location')}
      className="full-header custom-width-c with-border-header"
      title={t('Risk Mitigation')}
    >
      <Style>
        <div className="filters">
          <div className="filter">
            <div className="cont">
              <label>{t('Period')}</label>
            </div>
            <Select
              {...getPopupContainer()}
              showSearch
              filterOption={selectFilterSearch}
              value={period}
              onChange={(val) => setPeriod(val)}
            >
              {timelineOfIncidentsOptions.map((o) => (
                <Select.Option value={o.value} key={`options-${o.value}`}>
                  {t(o.label)}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="filter">
            <div className="cont">
              <label>{t('Risk Type')}</label>
            </div>
            <Select
              {...getPopupContainer()}
              showSearch
              filterOption={selectFilterSearch}
              value={riskType}
              onChange={(val) => setRiskType(val)}
            >
              <Select.Option value="all">
                {t('All risk types')}
              </Select.Option>
              {groupedOptions.map((o) => o.children ? (
                <Select.OptGroup
                  key={`${o.value}-risk-mitigation-filters`}
                  label={o.label}
                >
                  {o.children.map((ch) => (
                    <Select.Option
                      value={ch.value}
                      key={`${o.value}-risk-mitigation-filters-${ch.value}`}
                    >
                      {ch.label}
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              ) : (
                <Select.Option value={o.value} key={o.value}>
                  {o.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
        <div className="chart">
          <Chart
            period={period}
            data={filteredData}
            type="Measures"
            isSingleSource={isSingleSource}
          />
        </div>
        <Legend
          legend={legend}
          toggle={toggle}
          className="centered-legend"
        />
      </Style>
    </Widget>
  );
}

export default RiskMitigation;
